/**
 * Cards — GDD §5.
 *
 * The deck was built, rendered and shipped without ever being playable: the
 * client had no hand, and the engine had no way to draw or play. This module is
 * that missing half.
 *
 * A card's behaviour is DECLARATIVE data, never a callback. Two reasons:
 * content must stay addable as JSON (GOD-4 gate), and user-created cards (GOD-8)
 * must not be able to execute code.
 */

import type { DiceFace } from './dice.js';
import type { EffectSpec } from './encounter.js';
import type { CardId, Rarity } from './types.js';

export type CardType =
  | 'EVENT'
  | 'ACTION'
  | 'FATE'
  | 'KNOWLEDGE'
  | 'MEMORY'
  | 'RELATIONSHIP'
  | 'CURSE'
  | 'LEGEND';

/** Where a card may be played. `roll` means "while a roll is pending". */
export type CardContext = 'roll' | 'move' | 'encounter' | 'combat' | 'anytime';

export interface CardPlay {
  readonly in: readonly CardContext[];
  /** Force the pending roll to this face. The strongest effect in the game. */
  readonly setFace?: DiceFace;
  /** Nudge the pending roll, clamped to 1..6. */
  readonly faceDelta?: number;
  /** Store the current face in the Dice Bank. */
  readonly bankFace?: boolean;
  /** Extra movement points this turn. */
  readonly movementBonus?: number;
  /** Fate Debt charged (positive) or forgiven (negative). */
  readonly fateDebt?: number;
  /** Heal or harm. */
  readonly hp?: number;
  /** Reveal a specific secret, opening a hidden passage. */
  readonly revealSecret?: string;
  /** Anything else, through the shared effect compiler. */
  readonly effects?: readonly EffectSpec[];
  /** Remove from the deck entirely instead of discarding. One-shot cards. */
  readonly remove?: boolean;
}

/** The slice of a card the engine needs. Art and flavour stay in content. */
export interface CardVariant {
  /** `base+overlay` 의 오버레이 이름. 아트는 기본 아트 위에 겹쳐 그린다 (Style Bible §4). */
  readonly overlay: string;
  readonly name: string;
  readonly text: string;
}

export interface CardDefRef {
  readonly id: CardId;
  readonly type: CardType;
  readonly name: string;
  readonly text: string;
  readonly rarity: Rarity;
  readonly play?: CardPlay;
  readonly deprecated?: boolean;
  /** 변형판. 같은 그림에 오버레이만 얹어 다른 카드가 된다 — 새 일러스트가 필요 없다. */
  readonly variants?: readonly CardVariant[];
}

/**
 * `base+overlay` 를 그 변형판으로 해석한다. 오버레이가 없으면 원본 그대로.
 *
 * 전에는 `base+overlay` 가 그냥 기본 정의로 풀렸다. 그래서 변형 카드는 이름과
 * 설명이 원본으로 되돌아갔고, 무엇보다 **id 가 기본 id 로 바뀌어** 손에 없는
 * 카드가 되었다 — 내려고 하면 "손에 없다"는 예외가 났을 것이다.
 * `variants` 는 콘텐츠에 이미 쓰여 있는데 아무도 읽지 않고 있었다 (절대 규칙 11).
 */
export function resolveVariant(id: CardId, base: CardDefRef): CardDefRef {
  const overlay = cardOverlay(id);
  if (!overlay) return base;
  const variant = base.variants?.find((v) => v.overlay === overlay);
  // 정의되지 않은 오버레이라도 id 는 손에 있는 그대로 유지한다.
  return variant
    ? { ...base, id, name: variant.name, text: variant.text }
    : { ...base, id };
}

/** Cards with no `play` block are records, not actions — they cannot be played. */
export function isPlayable(card: CardDefRef | undefined, context: CardContext): boolean {
  if (!card?.play) return false;
  return card.play.in.includes(context) || card.play.in.includes('anytime');
}

/**
 * A transformed card keeps its base id before the `+`. Everything that resolves a
 * card — art, rules, the manifest — has to agree on that, so it lives here.
 */
export function baseCardId(id: CardId): CardId {
  return id.split('+')[0] ?? id;
}

export function cardOverlay(id: CardId): string | null {
  const parts = id.split('+');
  return parts.length > 1 ? (parts[1] ?? null) : null;
}

export const STARTING_HAND_SIZE = 3;
export const MAX_HAND_SIZE = 6;
